import { useMemo } from 'react';

import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { IAccount, IBusinessAttribute } from '@site-mate/sitemate-global-shared/lib';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { SettingsFormSkeleton } from '@/components/settings/settings-form-skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toaster';
import { usePermissions } from '@/hooks/use-permissions';
import { businessAttributeOtherOption } from '@/pages/signup/signup-form-utils';
import { getSitemateStartAccountQueryOptions } from '@/queries/account';
import { getBusinessRolesQueryOptions, getBusinessVerticalsQueryOptions } from '@/queries/settings';
import { workspaceSwitcherKeys } from '@/queries/workspace-switcher';
import { ApiService } from '@/services/api-v2';
import { workspaceService } from '@/services/global-api/workspace.service';
import { useUserStore } from '@/stores/user.store';

const formSchema = z.object({
  workspaceName: z.string().min(3),
  accountId: z.string().min(1),
  vertical: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .nullish(),
  role: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .nullish(),
});

function SettingsGeneralPage() {
  const { org } = useUserStore();

  const { data: account } = useQuery(getSitemateStartAccountQueryOptions({ accountId: org?.accountId }));

  const { data: verticals, isLoading: isLoadingVerticals } = useQuery(getBusinessVerticalsQueryOptions());

  const { data: roles, isLoading: isLoadingRoles } = useQuery(getBusinessRolesQueryOptions());

  const loading = useMemo(() => isLoadingVerticals || isLoadingRoles, [isLoadingVerticals, isLoadingRoles]);

  return (
    <div className="flex w-full max-w-[833px] flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
      <ScrollArea viewportClassName="pr-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-default-text text-xl font-medium">General</h1>
          {(loading || !verticals || !roles || !account) && <SettingsFormSkeleton rows={7} />}
          {verticals && roles && account && <SettingsGeneralForm verticals={verticals} roles={roles} account={account} />}
        </div>
      </ScrollArea>
    </div>
  );
}

function SettingsGeneralForm({
  verticals,
  roles,
  account,
}: {
  verticals: (IBusinessAttribute & { name: string })[];
  roles: (IBusinessAttribute & { name: string })[];
  account: IAccount;
}) {
  const { org, setOrg } = useUserStore();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { canEditWorkspaceSettings, isSuperUser } = usePermissions();

  const formInstance = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workspaceName: org?.name ?? '',
      accountId: org?.accountId ?? '',
      vertical: org?.vertical
        ? (() => {
            const vertical = verticals.find((v) => v._id === org.vertical);
            return vertical ? { label: vertical.name, value: vertical._id } : null;
          })()
        : null,
      role: org?.role
        ? (() => {
            const role = roles.find((r) => r._id === org.role);
            return role ? { label: role.name, value: role._id } : null;
          })()
        : null,
    },
  });
  const {
    control,
    handleSubmit,
    setError,
    formState: { isValid, isSubmitting, isDirty },
  } = formInstance;

  async function validateAccountIdChange(newAccountId: string) {
    try {
      await workspaceService.checkWorkspaceMigrationEligibility({
        fromAccountId: account!._id,
        toAccountId: newAccountId,
      });
      return true;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        switch (err.response?.status) {
          case 400:
          case 404:
            setError('accountId', {
              type: 'value',
              message: 'Sitemate Start Account could not be found with this ID.',
            });
            break;
          case 422:
            setError('accountId', {
              type: 'value',
              message: 'You cannot link to a Sitemate Start Account that is set to a different data region.',
            });
            break;
          default:
            setError('accountId', {
              type: 'value',
              message: 'Unknown error occurred while validating the Sitemate Start Account ID.',
            });
        }
      }
      return false;
    }
  }

  async function onSubmit(data: z.infer<typeof formSchema>) {
    if (!org) return;

    if (data.accountId !== org?.accountId) {
      const canMigrate = await validateAccountIdChange(data.accountId);

      if (!canMigrate) return;
    }

    try {
      const { workspace: updatedWorkspace } = await ApiService.UpdateWorkspace({
        workspaceId: org!.id,
        name: data.workspaceName,
        accountId: data.accountId,
        vertical: data.vertical?.value !== businessAttributeOtherOption.name ? data.vertical?.value : null,
        role: data.role?.value !== businessAttributeOtherOption.name ? data.role?.value : null,
      }).then((res) => res.json());
      org!.accountId = data.accountId;
      setOrg({
        ...org,
        ...{
          name: updatedWorkspace.name,
          accountId: updatedWorkspace.accountId!,
          vertical: updatedWorkspace.vertical,
          role: updatedWorkspace.role,
        },
      });

      await queryClient.invalidateQueries({ queryKey: workspaceSwitcherKeys.all });

      toast.success('Your settings have been updated successfully');
    } catch (err) {
      console.error('[SettingsGeneralForm] Failed to update workspace settings', {
        workspaceId: org?.id,
        error: err instanceof Error ? err.message : err,
      });
      toast.error('An error occurred while updating the settings.');
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <Controller
        control={control}
        name="workspaceName"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Workspace name</div>
            <Input
              placeholder="Enter workspace name..."
              value={value}
              onChange={(e) => onChange(e.currentTarget.value)}
              error={!!error}
              className="flex-1"
              disabled={!canEditWorkspaceSettings}
            />
          </Label>
        )}
      />

      <Label className="flex items-center gap-1">
        <div className="w-1/3 shrink-0">Workspace ID</div>
        <Input value={org?.workspaceId || ''} disabled placeholder="Workspace ID" className="flex-1" />
      </Label>

      <Controller
        control={control}
        name="accountId"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Sitemate Start Account ID</div>
            <Input
              placeholder="Enter Sitemate Start account ID..."
              value={value}
              onChange={(e) => onChange(e.currentTarget.value)}
              error={!!error}
              className="flex-1"
              disabled={!isSuperUser}
            />
          </Label>
        )}
      />

      <Label className="flex items-center gap-1">
        <div className="w-1/3 shrink-0">Sitemate Start Account Name</div>
        <Input value={account?.name || ''} disabled placeholder="Sitemate Start account name" className="flex-1" />
      </Label>

      <Controller
        control={control}
        name="vertical"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Industry</div>
            <Select
              aria-label="Industry"
              placeholder="Select industry..."
              className="flex-1"
              value={value}
              onChange={(value) => onChange(value)}
              options={verticals?.map((vertical) => ({ label: vertical.name, value: vertical._id })) ?? []}
              error={!!error}
              isDisabled={!canEditWorkspaceSettings}
            />
          </Label>
        )}
      />

      <Controller
        control={control}
        name="role"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Role</div>
            <Select
              aria-label="Role"
              placeholder="Select role..."
              className="flex-1"
              value={value}
              onChange={(value) => onChange(value)}
              options={roles?.map((role) => ({ label: role.name, value: role._id })) ?? []}
              error={!!error}
              isDisabled={!canEditWorkspaceSettings}
            />
          </Label>
        )}
      />

      {canEditWorkspaceSettings && (
        <div className="col-12">
          <Button type="submit" variant="success" className="mt-2" disabled={!isValid || isSubmitting || !isDirty} loading={isSubmitting}>
            Save changes
          </Button>
        </div>
      )}
    </form>
  );
}

export default SettingsGeneralPage;
